// Iter 91aj — Humi HRIS clone. Nested layout that renders under Conduit's
// normal chrome (Layout.jsx) with a Humi-specific sidebar on the left.
//
// Access control: Humi is staff-only + IP-locked at the API level. The
// frontend hides the whole /humi route from non-staff, and gracefully
// surfaces the API's 403 with a friendly "not on internal network" panel.

import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Home, User as UserIcon, Users as UsersIcon, CalendarClock, FileText,
  GraduationCap, ClipboardList, TrendingUp, BarChart3, ShieldCheck,
  Building2, Plug, ShieldAlert, RefreshCw,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/humi",             label: "Home",        icon: Home,           end: true,  testid: "humi-nav-home",        perm: "humi_home.view" },
  { to: "/humi/profile",     label: "Profile",     icon: UserIcon,                   testid: "humi-nav-profile",     perm: "humi_profile.view" },
  { to: "/humi/people",      label: "People",      icon: UsersIcon,                  testid: "humi-nav-people",      perm: "humi_people.view" },
  { to: "/humi/time-off",    label: "Time Off",    icon: CalendarClock,              testid: "humi-nav-time-off",    perm: "humi_time_off.view" },
  { to: "/humi/documents",   label: "Documents",   icon: FileText,                   testid: "humi-nav-documents",   perm: "humi_documents.view" },
  { to: "/humi/training",    label: "Training",    icon: GraduationCap,              testid: "humi-nav-training",    perm: "humi_training.view" },
  { to: "/humi/surveys",     label: "Surveys",     icon: ClipboardList,              testid: "humi-nav-surveys",     perm: "humi_surveys.view" },
  { to: "/humi/performance", label: "Performance", icon: TrendingUp,                 testid: "humi-nav-performance", perm: "humi_performance.view" },
  { to: "/humi/reporting",   label: "Reporting",   icon: BarChart3,                  testid: "humi-nav-reporting",   perm: "humi_reporting.view" },
  { to: "/humi/benefits",    label: "Benefits",    icon: ShieldCheck,                testid: "humi-nav-benefits",    perm: "humi_benefits.view" },
  { to: "/humi/company",     label: "Company",     icon: Building2,                  testid: "humi-nav-company",     perm: "humi_company.view" },
  { to: "/humi/addons",      label: "Add-ons",     icon: Plug,                       testid: "humi-nav-addons",      perm: "humi_addons.view" },
];

export default function HumiLayout() {
  const { user, has } = useAuth();
  const location = useLocation();
  const [gate, setGate] = useState({ loading: true, ok: false, error: "" });

  // Iter 115l — one permission per sidebar item; unpermitted sections vanish.
  const nav = NAV.filter((n) => has(n.perm));
  const current = [...NAV].sort((a, b) => b.to.length - a.to.length)
    .find((n) => location.pathname === n.to || location.pathname.startsWith(`${n.to}/`));
  const sectionAllowed = !current || has(current.perm);

  // Probe the API's guard once on mount so we can show a friendly "IP
  // restricted" panel instead of dumping a red toast onto every child page.
  useEffect(() => {
    let cancelled = false;
    api.get("/humi/access")
      .then(() => { if (!cancelled) setGate({ loading: false, ok: true, error: "" }); })
      .catch((e) => {
        if (cancelled) return;
        const status = e.response?.status;
        const msg = formatApiErrorDetail(e.response?.data?.detail) || "Access denied.";
        setGate({ loading: false, ok: false, error: msg, status });
      });
    return () => { cancelled = true; };
  }, [location.pathname]);

  if (user && user.base_role !== "staff") {
    return (
      <div className="space-y-4" data-testid="humi-not-staff">
        <PageHeader title="Humi" subtitle="Internal HR portal." />
        <Card className="p-8 text-center border-slate-200 dark:border-slate-700">
          <ShieldAlert className="mx-auto h-10 w-10 text-amber-500" />
          <div className="mt-3 text-lg font-semibold text-slate-800 dark:text-slate-100">Staff only</div>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Humi is UnitedCloud&apos;s internal HR portal and is not available to partners or customers.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_minmax(0,1fr)]" data-testid="humi-layout">
      <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="humi-sidebar">
        <Card className="p-2 border-slate-200 dark:border-slate-700">
          <nav className="flex flex-col gap-0.5">
            {nav.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `group flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition ${
                      isActive
                        ? "bg-primary/10 text-primary font-semibold"
                        : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                    }`
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{n.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </Card>
      </aside>

      <main className="min-w-0" data-testid="humi-main">
        {gate.loading ? (
          <Card className="flex items-center justify-center p-10 border-slate-200 dark:border-slate-700">
            <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
          </Card>
        ) : !gate.ok ? (
          <Card className="p-8 border-amber-200 bg-amber-50/60 dark:border-amber-500/30 dark:bg-amber-500/10" data-testid="humi-gate-denied">
            <div className="flex items-start gap-3">
              <ShieldAlert className="h-6 w-6 shrink-0 text-amber-500" />
              <div className="flex-1">
                <div className="text-base font-semibold text-slate-800 dark:text-slate-100">Humi access restricted</div>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{gate.error}</p>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  If you&apos;re on the UnitedCloud VPN and still see this, ask a super admin to add your egress IP to
                  <span className="mx-1 font-mono">Settings → Security → Humi allowlist</span>.
                </p>
                <Button size="sm" variant="outline" className="mt-3" onClick={() => window.location.reload()}>
                  <RefreshCw className="mr-1.5 h-3 w-3" /> Try again
                </Button>
              </div>
            </div>
          </Card>
        ) : !sectionAllowed ? (
          <Card className="p-8 text-center border-slate-200 dark:border-slate-700" data-testid="humi-section-denied">
            <ShieldAlert className="mx-auto h-10 w-10 text-amber-500" />
            <div className="mt-3 text-lg font-semibold text-slate-800 dark:text-slate-100">No access to {current?.label}</div>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Ask a super admin to grant you this Humi section.</p>
          </Card>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}
